import { useQuery } from '@tanstack/react-query';
import { getActiveAuction, getAuctionById, getAuctions, getPastAuctions, getSidebarProduct } from '../services/mockDataService';

// Query keys for cache management
export const auctionKeys = {
  all: ['auctions'] as const,
  lists: () => [...auctionKeys.all, 'list'] as const,
  list: (filters?: Record<string, any>) => [...auctionKeys.lists(), filters] as const,
  details: () => [...auctionKeys.all, 'detail'] as const,
  detail: (id: number) => [...auctionKeys.details(), id] as const,
  past: () => [...auctionKeys.all, 'past'] as const,
};

/**
 * Hook to fetch all auctions
 */
export const useAuctions = () => {
  return useQuery({
    queryKey: auctionKeys.lists(),
    queryFn: getAuctions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

/**
 * Hook to fetch a single auction by ID
 */
export const useAuction = (auctionId: number) => {
  return useQuery({
    queryKey: auctionKeys.detail(auctionId),
    queryFn: () => getAuctionById(auctionId),
    enabled: !!auctionId, // Only run if auctionId is provided
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to get the first/active auction
 * This replaces the old useGetAuction hook
 * Uses mockDataService for environment-based toggle
 */
export const useActiveAuction = () => {
  return useQuery({
    queryKey: [...auctionKeys.all, 'active'],
    queryFn: getActiveAuction,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch past/completed auctions only
 * Uses mockDataService for environment-based toggle
 */
export const usePastAuctions = () => {
  return useQuery({
    queryKey: auctionKeys.past(),
    queryFn: getPastAuctions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to get sidebar product for static pages (Blog, About Us, Contact, Help)
 * Uses mockDataService for environment-based toggle
 */
export const useSidebarAuction = () => {
  return useQuery({
    queryKey: [...auctionKeys.all, 'sidebar'],
    queryFn: getSidebarProduct,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
